import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronDown, ChevronRight, KeyRound, Loader2, Lock, Pencil, Plus, ShieldCheck } from "lucide-react";
import {
  ADMIN_CAPABILITIES,
  ADMIN_PERMISSION_PRESETS,
  type AdminCapability,
} from "@shared/admin-permissions";

// Admin Access Layer — super-admin-only management of admin users. Grants
// and revokes the lower-tier capabilities and triggers set-password emails.
// super_admin itself is read-only here by design: it is DB-only and the
// API rejects any attempt to edit a super-admin's row.

type AdminUserRow = {
  id: number;
  email: string;
  name: string | null;
  superAdmin: boolean;
  adminPermissions: string[];
  adminLastLoginAt: string | null;
  hasAdminPassword: boolean;
  pendingSetupTokenExpiresAt: string | null;
};

type LoginHistoryEntry = {
  id: number;
  success: boolean;
  reason: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
};

const CAPABILITY_LABELS: Record<AdminCapability, string> = {
  impersonate_users: "Impersonate users",
  manage_beta_access: "Manage Beta Access tool (not beta participation)",
  view_marketing_materials: "View marketing materials",
  manage_marketing_materials: "Manage marketing materials",
  upload_marketing_data: "Upload marketing data",
};

function presetForPermissions(perms: string[]): "SUPPORT" | "MARKETING" | null {
  const sorted = [...perms].sort().join(",");
  for (const [name, presetPerms] of Object.entries(ADMIN_PERMISSION_PRESETS)) {
    if ([...presetPerms].sort().join(",") === sorted) return name as "SUPPORT" | "MARKETING";
  }
  return null;
}

function CapabilityChecklist({
  value,
  onChange,
  idPrefix,
}: {
  value: AdminCapability[];
  onChange: (next: AdminCapability[]) => void;
  idPrefix: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {(Object.keys(ADMIN_PERMISSION_PRESETS) as Array<keyof typeof ADMIN_PERMISSION_PRESETS>).map((preset) => (
          <Button
            key={preset}
            type="button"
            size="sm"
            variant={presetForPermissions(value) === preset ? "default" : "outline"}
            onClick={() => onChange([...ADMIN_PERMISSION_PRESETS[preset]])}
            data-testid={`button-preset-${idPrefix}-${preset.toLowerCase()}`}
          >
            {preset === "SUPPORT" ? "Support" : "Marketing"} preset
          </Button>
        ))}
      </div>
      <div className="space-y-2">
        {ADMIN_CAPABILITIES.map((cap) => (
          <div key={cap} className="flex items-center gap-2">
            <Checkbox
              id={`${idPrefix}-${cap}`}
              checked={value.includes(cap)}
              onCheckedChange={(checked) =>
                onChange(checked ? [...value, cap] : value.filter((c) => c !== cap))
              }
              data-testid={`checkbox-${idPrefix}-${cap}`}
            />
            <Label htmlFor={`${idPrefix}-${cap}`} className="text-sm font-normal cursor-pointer">
              {CAPABILITY_LABELS[cap]}
            </Label>
          </div>
        ))}
      </div>
    </div>
  );
}

function LoginHistoryRows({ userId }: { userId: number }) {
  const historyQuery = useQuery<{ entries: LoginHistoryEntry[] }>({
    queryKey: [`/api/admin/admin-users/${userId}/login-history`],
  });
  if (historyQuery.isLoading) {
    return <Skeleton className="h-16 w-full" data-testid={`skeleton-login-history-${userId}`} />;
  }
  const entries = historyQuery.data?.entries ?? [];
  if (entries.length === 0) {
    return <div className="text-xs text-muted-foreground italic py-2">No admin logins recorded.</div>;
  }
  return (
    <div className="rounded border overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs py-1.5">Result</TableHead>
            <TableHead className="text-xs py-1.5">IP</TableHead>
            <TableHead className="text-xs py-1.5 text-right">Time</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e) => (
            <TableRow key={e.id} data-testid={`row-login-history-${userId}-${e.id}`}>
              <TableCell className="py-1 text-xs">
                <Badge variant={e.success ? "secondary" : "destructive"} className="text-[10px] px-1.5 py-0">
                  {e.success ? "Success" : "Failed"}
                </Badge>
              </TableCell>
              <TableCell className="py-1 text-xs font-mono">{e.ipAddress || "—"}</TableCell>
              <TableCell className="py-1 text-xs text-muted-foreground text-right whitespace-nowrap">
                {new Date(e.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function AdminUsersPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addPermissions, setAddPermissions] = useState<AdminCapability[]>([...ADMIN_PERMISSION_PRESETS.SUPPORT]);
  const [editTarget, setEditTarget] = useState<AdminUserRow | null>(null);
  const [editPermissions, setEditPermissions] = useState<AdminCapability[]>([]);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const usersQuery = useQuery<{ users: AdminUserRow[] }>({
    queryKey: ["/api/admin/admin-users"],
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/admin/admin-users"] });

  const addMutation = useMutation({
    mutationFn: (vars: { email: string; permissions: AdminCapability[] }) =>
      apiRequest("POST", "/api/admin/admin-users", vars),
    onSuccess: () => {
      toast({ title: "Admin user added", description: `${addEmail.trim()} can now access their granted areas.` });
      setAddOpen(false);
      setAddEmail("");
      setAddPermissions([...ADMIN_PERMISSION_PRESETS.SUPPORT]);
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Could not add admin user", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" });
    },
  });

  const editMutation = useMutation({
    mutationFn: (vars: { id: number; permissions: AdminCapability[] }) =>
      apiRequest("PATCH", `/api/admin/admin-users/${vars.id}/permissions`, { permissions: vars.permissions }),
    onSuccess: (_data, vars) => {
      toast({
        title: vars.permissions.length === 0 ? "Admin access revoked" : "Permissions updated",
        description: vars.permissions.length === 0 ? "The account can no longer log into the admin panel." : undefined,
      });
      setEditTarget(null);
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Could not update permissions", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" });
    },
  });

  const sendSetupMutation = useMutation({
    mutationFn: (vars: { id: number }) =>
      apiRequest("POST", `/api/admin/admin-users/${vars.id}/send-password-setup`),
    onSuccess: () => {
      toast({ title: "Setup email sent", description: "The single-use link expires in 24 hours." });
      invalidate();
    },
    onError: (err) => {
      toast({ title: "Could not send setup email", description: parseErrorMessage(err, "Something went wrong. Please try again."), variant: "destructive" });
    },
  });

  const users = usersQuery.data?.users ?? [];

  return (
    <Card className="p-6" data-testid="panel-admin-users" data-tour="admin-users-section">
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Admin Users
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Accounts that can sign into this admin panel. Grant the Support tier (impersonation) or
            Marketing tier (content &amp; data uploads), then send a password setup email. Super-admin
            status is managed directly in the database and is read-only here.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="button-add-admin-user">
          <Plus className="h-4 w-4 mr-1" /> Add admin user
        </Button>
      </div>

      {usersQuery.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table data-testid="table-admin-users">
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Account</TableHead>
                <TableHead>Access</TableHead>
                <TableHead>Admin password</TableHead>
                <TableHead>Last admin login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <Fragment key={u.id}>
                  <TableRow data-testid={`row-admin-user-${u.id}`}>
                    <TableCell className="py-2">
                      <button
                        type="button"
                        onClick={() => setExpandedUserId((cur) => (cur === u.id ? null : u.id))}
                        data-testid={`button-expand-admin-user-${u.id}`}
                        aria-label="Toggle login history"
                      >
                        {expandedUserId === u.id ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="font-medium">{u.email}</div>
                      {u.name && <div className="text-xs text-muted-foreground">{u.name}</div>}
                    </TableCell>
                    <TableCell className="py-2">
                      {u.superAdmin ? (
                        <Badge className="gap-1" data-testid={`badge-super-admin-${u.id}`}>
                          <Lock className="h-3 w-3" /> Super Admin
                        </Badge>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {presetForPermissions(u.adminPermissions) ? (
                            <Badge variant="secondary" data-testid={`badge-preset-${u.id}`}>
                              {presetForPermissions(u.adminPermissions) === "SUPPORT" ? "Support" : "Marketing"}
                            </Badge>
                          ) : (
                            u.adminPermissions.map((p) => (
                              <Badge key={p} variant="outline" className="text-[10px]">
                                {CAPABILITY_LABELS[p as AdminCapability] ?? p}
                              </Badge>
                            ))
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-sm">
                      {u.hasAdminPassword ? (
                        <span className="text-emerald-600">Set</span>
                      ) : u.pendingSetupTokenExpiresAt ? (
                        <span className="text-amber-600">Setup pending</span>
                      ) : (
                        <span className="text-muted-foreground">Not set</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-sm text-muted-foreground">
                      {u.adminLastLoginAt ? new Date(u.adminLastLoginAt).toLocaleString() : "Never"}
                    </TableCell>
                    <TableCell className="py-2 text-right">
                      {u.superAdmin ? (
                        <span className="text-xs text-muted-foreground italic">Managed in DB</span>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditTarget(u);
                              setEditPermissions(u.adminPermissions.filter((p): p is AdminCapability => (ADMIN_CAPABILITIES as readonly string[]).includes(p)));
                            }}
                            data-testid={`button-edit-admin-user-${u.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={sendSetupMutation.isPending}
                            onClick={() => sendSetupMutation.mutate({ id: u.id })}
                            title="Send password setup email"
                            data-testid={`button-send-setup-${u.id}`}
                          >
                            {sendSetupMutation.isPending && sendSetupMutation.variables?.id === u.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <KeyRound className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedUserId === u.id && (
                    <TableRow data-testid={`row-admin-user-history-${u.id}`}>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="py-2">
                          <div className="text-xs font-medium text-muted-foreground mb-1.5">Recent admin logins:</div>
                          <LoginHistoryRows userId={u.id} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    No admin users yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add dialog */}
      <Dialog
        open={addOpen}
        onOpenChange={(open) => {
          if (addMutation.isPending) return;
          setAddOpen(open);
        }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (addMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (addMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Add admin user</DialogTitle>
            <DialogDescription>
              The email must belong to an existing PadPulse account. After granting access, send them a
              password setup email so they can sign into the admin panel.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="add-admin-email">Account email</Label>
              <Input
                id="add-admin-email"
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="teammate@example.com"
                data-testid="input-add-admin-email"
              />
            </div>
            <CapabilityChecklist value={addPermissions} onChange={setAddPermissions} idPrefix="add" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} disabled={addMutation.isPending} data-testid="button-cancel-add-admin">
              Cancel
            </Button>
            <Button
              onClick={() => addMutation.mutate({ email: addEmail.trim(), permissions: addPermissions })}
              disabled={addMutation.isPending || !addEmail.trim() || addPermissions.length === 0}
              data-testid="button-confirm-add-admin"
            >
              {addMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Grant access"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog
        open={!!editTarget}
        onOpenChange={(open) => {
          if (editMutation.isPending) return;
          if (!open) setEditTarget(null);
        }}
      >
        <DialogContent
          onInteractOutside={(e) => { if (editMutation.isPending) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (editMutation.isPending) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>Edit permissions</DialogTitle>
            <DialogDescription>
              {editTarget?.email} — unchecking everything revokes admin panel access entirely.
            </DialogDescription>
          </DialogHeader>
          <CapabilityChecklist value={editPermissions} onChange={setEditPermissions} idPrefix="edit" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} disabled={editMutation.isPending} data-testid="button-cancel-edit-admin">
              Cancel
            </Button>
            <Button
              onClick={() => editTarget && editMutation.mutate({ id: editTarget.id, permissions: editPermissions })}
              disabled={editMutation.isPending}
              variant={editPermissions.length === 0 ? "destructive" : "default"}
              data-testid="button-confirm-edit-admin"
            >
              {editMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : editPermissions.length === 0 ? (
                "Revoke access"
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
